import { useRef, type KeyboardEvent } from 'react'

/**
 * Keys for a field saved on blur: Enter saves (by blurring), Escape puts the
 * value back and blurs *without* saving, and goes no further — the Year's own
 * window Escape closes the pane (UI-audit a4-05). A flag rather than handing
 * the old value to the save: `revert`'s setState has not re-rendered by the
 * time the blur fires in the same event, so the save would read the edited
 * text from its closure and write exactly what Escape meant to discard.
 *
 * Shared by desk Year's fields and the phone lists' inline edits (a task's
 * title, a grocery line), where Escape used to do nothing and blur always
 * saved (UI-audit a2-10).
 */
export function useBlurField(revert: () => void) {
  const cancelled = useRef(false)
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur()
    else if (e.key === 'Escape') {
      e.stopPropagation()
      cancelled.current = true
      revert()
      e.currentTarget.blur()
    }
  }
  const onBlurOf = (save: () => void) => () => {
    if (cancelled.current) { cancelled.current = false; return }
    save()
  }
  return { onKeyDown, onBlurOf }
}
